import React, { useState, useEffect } from 'react';
import { Dimmer } from 'semantic-ui-react';
import queryString from 'query-string';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { withRouter, useLocation, Link } from 'react-router-dom';
import { isArray } from 'lodash';
import { format } from 'date-fns';
import { DATE_FORMAT_DISPLAY, SUCCESS_SCREEN_PDF_REQUEST_LIMIT } from '../../constants';
import {
  getPaymentReceipt,
  getTravelRecurringPayments,
  recoverRecurringPayment,
  tryToPayAgain,
} from '../../redux/modules/payment';
import successContract from '../../styles/svgs/succesContract.svg';
import Currency from '../Currency';
import { errorTexts, PAYMENT_FAILED_ERROR_CODE } from './options';
import Spinner from '../Loaders/Spinner';
import MainButton from '../MUI/MainButton';
import { efesGray } from '../../theme';
import EfesContentHeader from '../EfesContentHeader';
import ErrorContent from './PageContent/ErrorContent';
import paymentIcon from '../../img/svgs/payment/contentCard.svg';
import useDeviceResize from '../../hooks/useDeviceResize';
import ScrollToTopPage from '../ScrollToTopPage';

import styles from './PaymentConfirmation.module.scss';
import useStyles from './makeStyles';

const RecurringPaymentConfirmation = () => {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const { t } = useTranslation();
  const location = useLocation();
  const classes = useStyles();

  const spinnerSize = useDeviceResize('lg') ? 80 : 50;

  const {
    receipt,
    loadingReceipt,
    recoveringRecurringPayment,
    gettingPaymentUrl,
    payingInProgress,
  } = useSelector((state) => state.paymentReducer, shallowEqual);

  const [remainingRequest, setRemainingRequest] = useState(SUCCESS_SCREEN_PDF_REQUEST_LIMIT);
  const [success, setSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState(false);

  useEffect(() => {
    let timer;
    const urlParams = queryString.parse(location.search);
    const urlSuccess = urlParams.success;
    const { amount } = urlParams;
    const paymentType = queryString.parse(location.search).type;

    const shouldDownloadPaymentReceipt = urlSuccess === '1' && remainingRequest && !loadingReceipt && !receipt;

    if (shouldDownloadPaymentReceipt) {
      timer = setTimeout(() => {
        dispatch(
          getPaymentReceipt({
            lang: i18n.language,
            paymentId: urlParams.paymentId,
            paymentType,
            amount,
          }),
        );
        setRemainingRequest((state) => state - 1);
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [
    dispatch,
    i18n.language,
    loadingReceipt,
    location.pathname,
    location.search,
    receipt,
    remainingRequest,
  ]);

  useEffect(() => {
    const urlParams = queryString.parse(location.search);
    const urlSuccess = urlParams.success;
    const urlError = urlParams.error;
    if (!urlSuccess || urlSuccess === '0') {
      setSuccess(false);
      if (urlError === PAYMENT_FAILED_ERROR_CODE) {
        setPaymentError(true);
        dispatch(recoverRecurringPayment(urlParams.id, urlParams.type));
      }
    } else if (urlSuccess === '1') {
      setSuccess(true);
    }
  }, [dispatch, location.search]);

  useEffect(() => {
    const urlParams = queryString.parse(location.search);
    const urlSuccess = urlParams.success;
    const urlPolicyNumbers = urlParams.id;
    const urlAmounts = urlParams.amount;
    const billingNumber = urlParams.billing_number;
    if (urlSuccess === '1') {
      const policyNumbers = isArray(urlPolicyNumbers) ? urlPolicyNumbers : [urlPolicyNumbers];
      const amounts = isArray(urlAmounts) ? urlAmounts : [urlAmounts];
      dispatch(getTravelRecurringPayments(policyNumbers, amounts, billingNumber));
    }
  }, [dispatch, location.search, location.pathname]);

  const tryAgain = () => {
    dispatch(tryToPayAgain());
  };

  let content;
  if (!success) {
    const urlParams = queryString.parse(location.search);
    const errorCode = urlParams.error;
    const error = errorTexts[errorCode] ? errorTexts[errorCode] : errorTexts.default;
    const errorTitle = t(error.title);
    const errorDescription = t(error.description);

    const isLoading = gettingPaymentUrl || payingInProgress;

    content = (
      <ErrorContent
        isLoading={isLoading}
        paymentError={paymentError}
        errorDescription={errorDescription}
        errorTitle={errorTitle}
        tryAgain={tryAgain}
        errorCode={errorCode}
      />
    );
  } else {
    content = (
      <div id={styles.confirmationScreen}>
        <div className={styles.paymentStatusBlock}>
          <img className={styles.icon} src={successContract} alt="" />
          <div>
            <div className={classes.statusTitle}>{t('paymentConfirmation.contractSuccessText')}</div>
            <div className={classes.contractSuccessSubText}>
              <div className={classes.subText}>{t('paymentConfirmation.receiptSummary.note')}</div>
            </div>
            <div className={styles.receiptSummary}>
              <ReceiptSummaries />
            </div>
            {!receipt && !!remainingRequest && (
              <div className={classes.loadingText}>
                <div>
                  <Spinner color={efesGray} width={spinnerSize} height={spinnerSize} />
                </div>
                <p>{t('paymentConfirmation.receiptWait')}</p>
              </div>
            )}
            {receipt && (
              <Link to="/">
                <MainButton variant="contained" color="primary" className={classes.startNewButton}>
                  {t('paymentConfirmation.finish')}
                </MainButton>
              </Link>
            )}

          </div>
        </div>

        {!receipt && remainingRequest === 0 && (
          <div className={styles.pdfFailedStatus}>{t('paymentConfirmation.receiptError')}</div>
        )}

        {receipt && (
          <div className={styles.pdfs}>
            <div className={styles.pdfBlock}>
              <iframe
                title="Receipt"
                id="recurringPaymentReceipt"
                frameBorder="0"
                src={`${receipt.url}&e=1`}
              />
            </div>
          </div>
        )}
      </div>
    );
  }
  return (
    <div>
      <ScrollToTopPage />
      {recoveringRecurringPayment && (
        <Dimmer active inverted>
          <div>
            <Spinner />
          </div>
        </Dimmer>
      )}
      <EfesContentHeader
        headerText="recurrPayment.headerTitle"
        headerImage={paymentIcon}
      />
      <div className={styles.container}>
        <div className={styles.content}>{content}</div>
      </div>
    </div>
  );
};

export default withRouter(RecurringPaymentConfirmation);

const ReceiptSummaries = () => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const summaries = useSelector((state) => state.paymentReducer.recurringPaymentsSummaries);
  const paymentType = queryString.parse(search).payment_type;
  if (!summaries) {
    return null;
  }
  return (
    <div>
      {summaries.map((receiptSummary) => (
        <ul key={receiptSummary.contractNumber}>
          <li>
            <span className={styles.receiptSummary_label}>
              {t('paymentConfirmation.receiptSummary.contractNumber')}
            </span>
            <span className={styles.receiptSummary_value}>{receiptSummary.contractNumber}</span>
          </li>
          {paymentType === 'idram' && (
            <li>
              <span className={styles.receiptSummary_label}>
                {t('paymentConfirmation.receiptSummary.billingNumber')}
              </span>
              <span className={styles.receiptSummary_value}>{receiptSummary.billingNumber}</span>
            </li>
          )}
          <li>
            <span className={styles.receiptSummary_label}>
              {t('paymentConfirmation.receiptSummary.paid')}
            </span>
            <span className={styles.receiptSummary_value}>
              <Currency value={Number(receiptSummary.paid)} withCurrencyLabel />
            </span>
          </li>
          <li>
            <span className={styles.receiptSummary_label}>
              {t('paymentConfirmation.receiptSummary.balance')}
            </span>
            <span className={styles.receiptSummary_value}>
              <Currency value={Number(receiptSummary.balance)} withCurrencyLabel />
            </span>
          </li>
          {receiptSummary.nextPaymentDate && (
            <li>
              <span className={styles.receiptSummary_label}>
                {t('paymentConfirmation.receiptSummary.nextPayment')}
              </span>
              <span className={styles.receiptSummary_value}>
                {format(new Date(receiptSummary.nextPaymentDate), DATE_FORMAT_DISPLAY)}
              </span>
            </li>
          )}
        </ul>
      ))}
    </div>
  );
};
